import type { AxiosRequestConfig } from 'axios';
import { ApiError } from './httpClientFactory';
import { apiClient } from './client';
import type {
  CategoryItem as WireCategoryItem,
  CategoryRef as WireCategoryRef,
  FormDetail as WireFormDetail,
  FormPage as WireFormPage,
  FormSummary as WireFormSummary,
  PageItem as WirePageItem,
  PageManifest as WirePageManifest,
} from './generated';

export type CategoryRef = Required<Pick<WireCategoryRef, 'id' | 'name'>>;
export type CategoryItem = Required<Pick<WireCategoryItem, 'id' | 'name' | 'formCount'>>;
export type FormKind = Required<WireFormSummary>['kind'];
export type FormSummary = Required<
  Pick<
    WireFormSummary,
    'id' | 'code' | 'title' | 'kind' | 'renditions' | 'versionNo' | 'publishedAt'
  >
> & { categories: CategoryRef[] };
export type FormPage = { items: FormSummary[]; nextCursor: Required<WireFormPage>['nextCursor'] };
export type FormDetail = FormSummary &
  Required<
    Pick<
      WireFormDetail,
      | 'description'
      | 'company'
      | 'owner'
      | 'owningDepartment'
      | 'secondaryContact'
      | 'orderInstructions'
      | 'logo'
      | 'adaptiveUrl'
      | 'pageCount'
    >
  >;
export type PageItem = Required<Pick<WirePageItem, 'pageNo' | 'width' | 'height'>>;
export type PageManifest = Required<Pick<WirePageManifest, 'versionNo' | 'pageCount'>> & {
  pages: PageItem[];
};
export type Rendition = 'print' | 'interactive' | 'dynamic';

export const PAGE_SIZE = 50;

const KINDS: readonly FormKind[] = ['XDP', 'ADAPTIVE'];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function reqString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function reqNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function positiveInt(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isInteger(value) && value >= 1 ? value : undefined;
}

function invalid(status: number): never {
  throw new ApiError('INVALID_RESPONSE', status);
}

function requestConfig(
  signal: AbortSignal | undefined,
  params?: Record<string, string | number>,
): AxiosRequestConfig {
  const config: AxiosRequestConfig = {};
  if (signal !== undefined) config.signal = signal;
  if (params !== undefined) config.params = params;
  return config;
}

function parseCategoryRef(value: unknown): CategoryRef | null {
  if (!isRecord(value)) return null;
  const id = reqNumber(value.id);
  const name = reqString(value.name);
  if (id === undefined || name === undefined) return null;
  return { id, name };
}

function parseCategoryItem(value: unknown): CategoryItem | null {
  if (!isRecord(value)) return null;
  const id = reqNumber(value.id);
  const name = reqString(value.name);
  const formCount = reqNumber(value.formCount);
  if (id === undefined || name === undefined || formCount === undefined) return null;
  return { id, name, formCount };
}

function parseFormSummary(value: unknown): FormSummary | null {
  if (!isRecord(value)) return null;
  const id = reqNumber(value.id);
  const code = reqString(value.code);
  const title = reqString(value.title);
  const versionNo = reqNumber(value.versionNo);
  const publishedAt = reqString(value.publishedAt);
  const kind = value.kind;
  if (
    id === undefined ||
    code === undefined ||
    title === undefined ||
    versionNo === undefined ||
    publishedAt === undefined ||
    typeof kind !== 'string' ||
    !KINDS.includes(kind as FormKind) ||
    !Array.isArray(value.categories) ||
    !Array.isArray(value.renditions)
  ) {
    return null;
  }
  const categories: CategoryRef[] = [];
  for (const entry of value.categories) {
    const category = parseCategoryRef(entry);
    if (!category) return null;
    categories.push(category);
  }
  const renditions: string[] = [];
  for (const entry of value.renditions) {
    if (typeof entry !== 'string') return null;
    renditions.push(entry);
  }
  return {
    id,
    code,
    title,
    kind: kind as FormKind,
    categories,
    renditions,
    versionNo,
    publishedAt,
  };
}

function parseNextCursor(data: Record<string, unknown>): string | null | undefined {
  if (!Object.hasOwn(data, 'nextCursor')) return null;
  const value = data.nextCursor;
  if (value === null || typeof value === 'string') return value;
  return undefined;
}

function parseFormDetail(value: unknown): FormDetail | null {
  const summary = parseFormSummary(value);
  if (!summary || !isRecord(value)) return null;
  const description = reqString(value.description);
  const company = reqString(value.company);
  const owner = reqString(value.owner);
  const owningDepartment = reqString(value.owningDepartment);
  const secondaryContact = reqString(value.secondaryContact);
  const orderInstructions = reqString(value.orderInstructions);
  if (
    description === undefined ||
    company === undefined ||
    owner === undefined ||
    owningDepartment === undefined ||
    secondaryContact === undefined ||
    orderInstructions === undefined ||
    typeof value.logo !== 'boolean' ||
    !(typeof value.adaptiveUrl === 'string' || value.adaptiveUrl === null)
  ) {
    return null;
  }
  let pageCount: number | null = null;
  if (value.pageCount !== undefined && value.pageCount !== null) {
    const count = positiveInt(value.pageCount);
    if (count === undefined) return null;
    pageCount = count;
  }
  return {
    ...summary,
    description,
    company,
    owner,
    owningDepartment,
    secondaryContact,
    orderInstructions,
    logo: value.logo,
    adaptiveUrl: value.adaptiveUrl,
    pageCount,
  };
}

function parsePageManifest(value: unknown): PageManifest | null {
  if (!isRecord(value)) return null;
  const versionNo = positiveInt(value.versionNo);
  const pageCount = positiveInt(value.pageCount);
  if (versionNo === undefined || pageCount === undefined || !Array.isArray(value.pages)) {
    return null;
  }
  if (value.pages.length !== pageCount) return null;
  const pages: PageItem[] = [];
  for (const [index, entry] of value.pages.entries()) {
    if (!isRecord(entry)) return null;
    const width = positiveInt(entry.width);
    const height = positiveInt(entry.height);
    if (entry.pageNo !== index + 1 || width === undefined || height === undefined) return null;
    pages.push({ pageNo: index + 1, width, height });
  }
  return { versionNo, pageCount, pages };
}

export async function listCategories(signal?: AbortSignal): Promise<CategoryItem[]> {
  const res = await apiClient.get<unknown>('/categories', requestConfig(signal));
  if (!isRecord(res.data) || !Array.isArray(res.data.items)) invalid(res.status);
  const items: CategoryItem[] = [];
  for (const entry of res.data.items) {
    const item = parseCategoryItem(entry);
    if (!item) invalid(res.status);
    items.push(item);
  }
  return items;
}

export async function searchForms(p: {
  q: string;
  categoryId: number | null;
  cursor: string | null;
  signal?: AbortSignal;
}): Promise<FormPage> {
  const params: Record<string, string | number> = { limit: PAGE_SIZE };
  const q = p.q.trim();
  if (q !== '') params.q = q;
  if (p.categoryId !== null) params.categoryId = p.categoryId;
  if (p.cursor !== null) params.cursor = p.cursor;
  const res = await apiClient.get<unknown>('/forms', requestConfig(p.signal, params));
  if (!isRecord(res.data) || !Array.isArray(res.data.items)) invalid(res.status);
  const items: FormSummary[] = [];
  for (const entry of res.data.items) {
    const item = parseFormSummary(entry);
    if (!item) invalid(res.status);
    items.push(item);
  }
  const nextCursor = parseNextCursor(res.data);
  if (nextCursor === undefined) invalid(res.status);
  return { items, nextCursor };
}

export async function getForm(id: number, signal?: AbortSignal): Promise<FormDetail> {
  const res = await apiClient.get<unknown>(`/forms/${id}`, requestConfig(signal));
  const detail = parseFormDetail(res.data);
  if (!detail) invalid(res.status);
  return detail;
}

/** `GET /forms/{id}/pages`. Validates order, count and sizes; anything else is INVALID_RESPONSE. */
export async function getFormPages(id: number, signal?: AbortSignal): Promise<PageManifest> {
  const res = await apiClient.get<unknown>(`/forms/${id}/pages`, requestConfig(signal));
  const manifest = parsePageManifest(res.data);
  if (!manifest) invalid(res.status);
  return manifest;
}

/** `GET /forms/{id}/versions/{versionNo}/pages/{pageNo}` as a Blob. */
export async function getPageImage(p: {
  id: number;
  versionNo: number;
  pageNo: number;
  signal?: AbortSignal;
}): Promise<Blob> {
  const res = await apiClient.get<unknown>(
    `/forms/${p.id}/versions/${p.versionNo}/pages/${p.pageNo}`,
    { ...requestConfig(p.signal), responseType: 'blob' },
  );
  if (!(res.data instanceof Blob)) invalid(res.status);
  return res.data;
}

/** `GET /forms/{id}/files/{rendition}` as a Blob. */
export async function getFormFile(
  id: number,
  rendition: Rendition,
  signal?: AbortSignal,
): Promise<Blob> {
  const res = await apiClient.get<unknown>(`/forms/${id}/files/${rendition}`, {
    ...requestConfig(signal),
    responseType: 'blob',
  });
  if (!(res.data instanceof Blob)) invalid(res.status);
  return res.data;
}

/** `POST /forms/{id}/views`; resolves on 204. */
export async function recordFormView(id: number): Promise<void> {
  await apiClient.post(`/forms/${id}/views`);
}

/** The lower-case path segment of a served rendition; anything the app doesn't offer is null. */
export function renditionOf(served: string): Rendition | null {
  switch (served) {
    case 'PRINT':
      return 'print';
    case 'INTERACTIVE':
      return 'interactive';
    case 'DYNAMIC':
      return 'dynamic';
    default:
      return null;
  }
}
